/**
 * Google Merchant Center store widget. Consent policy: docs/cookie-banner.md.
 * Titles the badge iframe after start() (WCAG 4.1.2).
 *
 * Upstream: none — Mysa first-party glue for the GMC widget script.
 */
import { marketingAndSaleDecision } from "@theme/customer-privacy";
const SUPPORTED_REGIONS = ["CA", "US"];

const WIDGET_DEFAULTS = {
  position: "RIGHT_BOTTOM",
  sideMargin: 36,
  bottomMargin: 56,
  mobileSideMargin: 16,
  mobileBottomMargin: 80,
};

const WIDGET_IFRAME_TITLE = "Google Store";
const WIDGET_IFRAME_OBSERVE_MS = 10000;
const MERCHANT_WIDGET_SRC =
  "https://www.gstatic.com/shopping/merchant/merchantwidget.js";

const BUY_BOX_SELECTOR = ".buy-box-wrapper";
const MOBILE_BUY_BOX_QUERY = "(max-width: 767px)";
const DESKTOP_QUERY = "(min-width: 768px)";
const BUY_BOX_CLEARANCE_PX = 16;

/**
 * Mobile is the common case, so WIDGET_DEFAULTS already puts the badge
 * bottom-right and only desktop overrides the position, moving it bottom-left
 * so it never lands on the sticky buy column.
 *
 * Bottom clearance applies to both viewports: `merchantwidget` picks between
 * `bottomMargin` and `mobileBottomMargin` on its own breakpoint, which need not
 * match the theme's 767px fixed-bar breakpoint, so a computed clearance is
 * always written to both keys. Only the mobile fixed bar is measured — the
 * desktop buy column is full-height, and using it as a bottom margin would
 * launch the badge up the page — so desktop keeps the WIDGET_DEFAULTS margins,
 * which the spread in `initGoogleStoreWidget` preserves on both keys.
 *
 * `merchantwidget` pins `bottom` inline with `!important` — no stylesheet rule
 * can override that — so this margin is the only lever, and it is read once at
 * `start()`. Expanding the pricing details grows the bar past this clearance;
 * mysa-buy-box.liquid hides the badge for that state.
 * @returns {{ position?: string, bottomMargin?: number, mobileBottomMargin?: number }}
 */
const resolveWidgetLayout = () => {
  const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
  if (isDesktop) return { position: "LEFT_BOTTOM" };
  if (!window.matchMedia(MOBILE_BUY_BOX_QUERY).matches) return {};

  const buyBox = document.querySelector(BUY_BOX_SELECTOR);
  if (!(buyBox instanceof HTMLElement)) return {};

  const { height } = buyBox.getBoundingClientRect();
  if (height <= 0) return {};

  const margin = Math.ceil(height) + BUY_BOX_CLEARANCE_PX;
  return { bottomMargin: margin, mobileBottomMargin: margin };
};

/**
 * @param {HTMLIFrameElement} iframe
 * @returns {boolean}
 */
const isMerchantWidgetIframe = (iframe) => {
  const src = iframe.getAttribute("src") ?? "";
  const id = iframe.id?.toLowerCase() ?? "";
  const className =
    typeof iframe.className === "string" ? iframe.className.toLowerCase() : "";

  // Prefer id/class (e.g. #merchantwidgetiframe). For src, require a Google host
  // AND a merchant/shopping path so Pay/reCAPTCHA/Maps embeds are not retitled.
  const isGoogleHost =
    src.includes("gstatic.com") ||
    src.includes("google.com") ||
    src.includes("googleusercontent.com");
  const isMerchantPath = src.includes("/shopping") || src.includes("/merchant");

  return (
    id.includes("merchant") ||
    className.includes("merchant") ||
    (isGoogleHost && isMerchantPath)
  );
};

/**
 * @param {ParentNode} [root]
 */
const ensureWidgetIframeTitles = (root = document) => {
  for (const iframe of root.querySelectorAll("iframe")) {
    if (!(iframe instanceof HTMLIFrameElement)) continue;
    if (!isMerchantWidgetIframe(iframe)) continue;
    if (iframe.getAttribute("title")) continue;
    iframe.setAttribute("title", WIDGET_IFRAME_TITLE);
  }
};

/**
 * Watch briefly for iframes the merchant widget injects after start().
 * @returns {() => void} disconnect
 */
const observeWidgetIframes = () => {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node instanceof HTMLIFrameElement) {
          if (isMerchantWidgetIframe(node) && !node.getAttribute("title")) {
            node.setAttribute("title", WIDGET_IFRAME_TITLE);
          }
          continue;
        }
        ensureWidgetIframeTitles(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  window.setTimeout(() => observer.disconnect(), WIDGET_IFRAME_OBSERVE_MS);
  return () => observer.disconnect();
};

const merchantWidgetApi = () => {
  const api = window.merchantwidget;
  if (!api || typeof api.start !== "function") return null;

  return api;
};

/**
 * @param {HTMLElement} scriptEl
 * @returns {boolean}
 */
const initGoogleStoreWidget = (scriptEl) => {
  const api = merchantWidgetApi();
  if (!api) {
    console.warn("Google store widget script not loaded");
    return false;
  }

  /** @type {Record<string, string | number>} */
  const startOptions = { ...WIDGET_DEFAULTS, ...resolveWidgetLayout() };

  const region = scriptEl.dataset.region?.toUpperCase() ?? "";
  if (SUPPORTED_REGIONS.includes(region)) {
    startOptions.region = region;
  }

  const language = scriptEl.dataset.language?.toLowerCase() ?? "";
  if (language) {
    startOptions.language = language;
  }

  api.start(startOptions);
  ensureWidgetIframeTitles();
  observeWidgetIframes();
  return true;
};

const MERCHANT_SCRIPT_TIMEOUT_MS = 10000;

let runId = 0;
let widgetActive = false;

/** @type {Promise<void> | null} */
let merchantScriptLoad = null;

const removeMerchantWidgetDom = () => {
  document.getElementById("google-merchantwidget-iframe-wrapper")?.remove();
  document.getElementById("merchantwidgetiframe")?.remove();
};

/**
 * A finished script tag does not fire load again. Drop any leftover tag
 * before injecting, and drop it again when the attempt fails, so the next
 * consent change can request a fresh one. A denial does not call this.
 * @returns {Promise<void>}
 */
const injectMerchantScript = () =>
  new Promise((resolve, reject) => {
    document.getElementById("merchantWidgetScript")?.remove();

    const script = document.createElement("script");
    script.id = "merchantWidgetScript";
    script.src = MERCHANT_WIDGET_SRC;

    let settled = false;
    const timer = window.setTimeout(() => {
      settle(new Error("Google store widget script timed out"));
    }, MERCHANT_SCRIPT_TIMEOUT_MS);

    const settle = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (!error) {
        resolve();
        return;
      }

      script.remove();
      reject(error);
    };

    script.addEventListener(
      "load",
      () => {
        if (!merchantWidgetApi()) {
          settle(
            new Error(
              "Google store widget script loaded without merchantwidget",
            ),
          );
          return;
        }

        settle(null);
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        settle(new Error("Google store widget script failed"));
      },
      { once: true },
    );
    document.head.appendChild(script);
  });

/**
 * One shared load. The first failure removes the tag and tries once more.
 * Callers that arrive while a load is in flight share that promise.
 * @returns {Promise<void>}
 */
const loadMerchantScript = () => {
  if (merchantWidgetApi()) return Promise.resolve();
  if (merchantScriptLoad) return merchantScriptLoad;

  merchantScriptLoad = injectMerchantScript()
    .catch(() => {
      if (merchantWidgetApi()) return undefined;
      return injectMerchantScript();
    })
    .finally(() => {
      merchantScriptLoad = null;
    });

  return merchantScriptLoad;
};

const syncMerchantWidget = async () => {
  const config = document.getElementById("merchantWidgetConfig");
  if (!(config instanceof HTMLElement)) return;

  const run = ++runId;
  let decision = "denied";
  try {
    decision = window.Shopify?.designMode
      ? "allowed"
      : await marketingAndSaleDecision();
  } catch (error) {
    console.warn("Google store widget could not read consent", error);
  }
  if (run !== runId) return;

  config.dataset.merchantWidgetDecision =
    decision === "allowed" ? "allowed" : "blocked";

  if (decision !== "allowed") {
    widgetActive = false;
    removeMerchantWidgetDom();
    return;
  }

  if (
    widgetActive &&
    document.getElementById("google-merchantwidget-iframe-wrapper")
  ) {
    return;
  }

  try {
    await loadMerchantScript();
  } catch (error) {
    console.warn("Google store widget failed to load", error);
    return;
  }

  if (run !== runId) return;

  // Google throws if the wrapper is already in the document.
  removeMerchantWidgetDom();
  widgetActive = false;

  try {
    if (!initGoogleStoreWidget(config)) return;
  } catch (error) {
    console.warn(`Google store widget failed to start: ${error}`);
    removeMerchantWidgetDom();
    return;
  }

  if (run !== runId) {
    removeMerchantWidgetDom();
    return;
  }

  widgetActive = true;
};

const merchantWidgetConfig = document.getElementById("merchantWidgetConfig");
if (merchantWidgetConfig) {
  document.addEventListener("visitorConsentCollected", () => {
    void syncMerchantWidget();
  });
  void syncMerchantWidget();
}
